import { useState } from 'react'
import Zusammenlegen from '../../komponenten/Zusammenlegen'
import { ApiFehler } from '../../lib/api'
import { HERKUNFT_TEXT, holeDubletten, type Dublettenpaar } from '../../lib/kennzahlen'
import { zusammenlegen } from '../../lib/warteschlangen'

/**
 * Welche Orte im Bestand sind vermutlich derselbe?
 *
 * **Nur auf Knopfdruck.** Die Abfrage vergleicht 163.000 Orte mit sich selbst
 * und braucht dafür rund 15 Sekunden — der Plan ist dabei richtig, es sind
 * schlicht 150.908 Index-Suchen. In einen Seitenaufbau gehört das nicht.
 *
 * **Fehltreffer sind eingeplant**, etwa zwei von drei. Deshalb entscheidet
 * hier nichts von selbst: Der Bildschirm führt zu beiden Orten, und erst wer
 * sie angesehen hat, legt sie zusammen.
 *
 * **Seit dem 29.09.2026 geht das hier direkt** — vorher musste eine Dublette
 * erst über App oder Website gemeldet werden, um sie dann in der Warteschlange
 * selbst zu entscheiden: ein Umweg über das eigene Nutzerkonto, der nichts
 * beitrug. Nachvollziehbar bleibt es trotzdem, `ort_zusammenlegen()` schreibt
 * seine eigene Zeile in `core_decision`.
 *
 * Die Sicherungen sind dieselben wie in der Meldungsbearbeitung: Sieger
 * ausdrücklich wählen, Vorschau vorher, Bestätigung danach. Bei zwei
 * Fehltreffern auf drei Funde wäre ein Knopf ohne Rückfrage fahrlässig.
 */
type Zustand =
  | { art: 'ruht' }
  | { art: 'laeuft' }
  | { art: 'da'; paare: Dublettenpaar[]; dauerMs: number }
  | { art: 'fehler'; meldung: string }

export default function Dubletten() {
  const [umkreis, setUmkreis] = useState(120)
  const [aehnlichkeit, setAehnlichkeit] = useState(0.45)
  const [zustand, setZustand] = useState<Zustand>({ art: 'ruht' })

  async function suchen() {
    setZustand({ art: 'laeuft' })
    try {
      const w = await holeDubletten(umkreis, aehnlichkeit)
      setZustand({ art: 'da', paare: w.paare, dauerMs: w.dauerMs })
    } catch (f) {
      setZustand({
        art: 'fehler',
        meldung:
          f instanceof ApiFehler || f instanceof Error ? f.message : 'Unbekannter Fehler',
      })
    }
  }

  return (
    <section className="stapel">
      <div className="stapel-kopf">
        <h1>Dublettenverdacht</h1>
        {zustand.art === 'da' && (
          <p className="gedaempft zahl">
            {zustand.paare.length} Paare
            {zustand.paare.length === 200 && <span className="leise"> · gedeckelt</span>}
            <span className="leise"> · {(zustand.dauerMs / 1000).toFixed(1)} s</span>
          </p>
        )}
      </div>

      <p className="meldung meldung-warnung">
        <strong>Rechne mit Fehltreffern — etwa zwei von drei.</strong>{' '}
        „Schulstraße" und „Schulweg" liegen zwölf Meter auseinander und ähneln
        sich zu 0,56; derselbe Platz aus zwei Quellen sieht genauso aus. Dieser
        Bildschirm entscheidet nichts. <strong>Die Namen führen zu beiden Orten
        auf kinderspiel.app</strong> — ansehen musst du sie.
      </p>

      {/* Wo es hingeht, wenn ein Paar wirklich eines ist. Ohne diesen Satz
          endet der Bildschirm in einer Sackgasse: Er zeigt etwas, wogegen sich
          nichts tun lässt. */}
      <p className="leise">
        Ist es wirklich dieselbe Stelle, gehört eine <strong>Meldung „Dublette"
        über die App oder kinderspiel.app</strong> dazu. Sie landet in der
        Warteschlange „Meldungen"; dort steht dann „Dublette zusammenlegen" und
        die Auswahl, welcher Ort bleibt. Das Dashboard kann keine Meldung
        selbst anlegen — es entscheidet über Meldungen, es schreibt keine.
      </p>

      <div className="karte filterzeile">
        <label className="feld">
          <span>Umkreis in Metern</span>
          <input
            type="number"
            min={10}
            max={500}
            step={10}
            value={umkreis}
            onChange={(e) => setUmkreis(Number(e.target.value) || 120)}
          />
        </label>
        <label className="feld">
          <span>Namensähnlichkeit ab</span>
          <input
            type="number"
            min={0.1}
            max={1}
            step={0.05}
            value={aehnlichkeit}
            onChange={(e) => setAehnlichkeit(Number(e.target.value) || 0.45)}
          />
        </label>
        <button
          className="knopf knopf-haupt"
          type="button"
          disabled={zustand.art === 'laeuft'}
          onClick={() => void suchen()}
        >
          {zustand.art === 'laeuft' ? 'Sucht … (bis 20 s)' : 'Suchen'}
        </button>
      </div>

      {zustand.art === 'ruht' && (
        <div className="karte">
          <p className="gedaempft">
            Die Suche vergleicht 163.000 Orte mit sich selbst und braucht dafür
            etwa 15 Sekunden. Deshalb läuft sie erst auf Knopfdruck.
          </p>
        </div>
      )}
      {zustand.art === 'laeuft' && (
        <div className="karte">
          <p className="gedaempft">Vergleicht den Bestand mit sich selbst …</p>
        </div>
      )}
      {zustand.art === 'fehler' && (
        <div className="karte">
          <p className="meldung meldung-fehler" role="alert">
            {zustand.meldung}
          </p>
        </div>
      )}

      {zustand.art === 'da' &&
        (zustand.paare.length === 0 ? (
          <div className="karte">
            <p className="gedaempft">Kein Paar passt zu diesen Werten.</p>
          </div>
        ) : (
          <ul className="paar-liste">
            {zustand.paare.map((p) => (
              <Paar
                key={`${p.id_a}:${p.id_b}`}
                p={p}
                erledigt={(ids) =>
                  setZustand((z) =>
                    z.art === 'da'
                      ? {
                          ...z,
                          // Alle Paare mit dem gelöschten Ort fallen weg — sie
                          // zeigten auf etwas, das es nicht mehr gibt.
                          paare: z.paare.filter(
                            (q) => !ids.includes(q.id_a) && !ids.includes(q.id_b),
                          ),
                        }
                      : z,
                  )
                }
              />
            ))}
          </ul>
        ))}
    </section>
  )
}

function Paar({
  p,
  erledigt,
}: {
  p: Dublettenpaar
  erledigt: (geloeschte: string[]) => void
}) {
  const [offen, setOffen] = useState(false)
  const [sieger, setSieger] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function legeZusammen() {
    if (!sieger) return
    const verlierer = sieger === p.id_a ? p.id_b : p.id_a
    setLaeuft(true)
    setFehler(null)
    try {
      await zusammenlegen(verlierer, sieger)
      erledigt([verlierer])
    } catch (f) {
      setFehler(f instanceof ApiFehler || f instanceof Error ? f.message : 'Unbekannter Fehler')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <li className="karte paar">
      <div className="paar-kopf">
        <span className="zahl nachbar-meter">{p.meter} m</span>
        <span className="leise">
          Namensähnlichkeit <span className="zahl">{p.namensaehnlichkeit}</span>
        </span>
      </div>
      <div className="paar-orte">
        <Ort
          name={p.name_a}
          strasse={p.strasse_a}
          stadt={p.stadt_a}
          quelle={p.quelle_a}
          bilder={p.bilder_a}
          id={p.id_a}
        />
        <Ort
          name={p.name_b}
          strasse={p.strasse_b}
          stadt={p.stadt_b}
          quelle={p.quelle_b}
          bilder={p.bilder_b}
          id={p.id_b}
        />
      </div>

      {!offen ? (
        <div className="knopf-reihe">
          <button
            className="knopf knopf-still"
            type="button"
            onClick={() => setOffen(true)}
          >
            Zusammenlegen …
          </button>
        </div>
      ) : (
        <>
          <Zusammenlegen
            gemeldeterOrt={p.id_a}
            zwilling={p.id_b}
            sieger={sieger}
            setzeSieger={setSieger}
            markiereErsten={false}
            ueberschrift="Welcher Ort bleibt?"
          />

          {fehler && (
            <p className="meldung meldung-fehler" role="alert">
              {fehler}
            </p>
          )}

          <div className="knopf-reihe">
            <button
              className="knopf knopf-ab"
              type="button"
              disabled={!sieger || laeuft}
              onClick={() => void legeZusammen()}
            >
              {laeuft ? 'Legt zusammen …' : 'Endgültig zusammenlegen'}
            </button>
            <button
              className="knopf knopf-still"
              type="button"
              onClick={() => setOffen(false)}
            >
              Abbrechen
            </button>
          </div>
        </>
      )}
    </li>
  )
}

/**
 * Ein Ort des Paares — mit dem Weg zu ihm.
 *
 * Der Bildschirm versprach von Anfang an, „zu beiden Orten zu führen"; bis
 * zum 29.09.2026 stand dort nur die UUID. Mit Name, Entfernung und Ähnlichkeit
 * allein lässt sich kein Paar beurteilen: „Schulstraße" und „Schulweg" sehen
 * wie eine Dublette aus und sind zwei Plätze, derselbe Platz aus zwei Quellen
 * sieht genauso aus. Das entscheidet nur, wer beide ansieht.
 */
function Ort({
  name,
  strasse,
  stadt,
  quelle,
  bilder,
  id,
}: {
  name: string | null
  strasse: string | null
  stadt: string | null
  quelle: string | null
  bilder: number
  id: string
}) {
  return (
    <div className="paar-ort">
      <p className="mensch-name">
        <a
          href={`https://kinderspiel.app/p/${id}`}
          target="_blank"
          rel="noreferrer noopener"
          title="Auf kinderspiel.app ansehen"
        >
          {name ?? 'ohne Namen'}
        </a>
      </p>
      <p className="gedaempft">
        {[strasse, stadt].filter(Boolean).join(', ') || 'keine Adresse hinterlegt'}
      </p>
      <p className="leise">
        {quelle ? (HERKUNFT_TEXT[quelle] ?? quelle) : 'Quelle unbekannt'}
        {' · '}
        {/* Bilder sind das, woran man zwei Plätze am schnellsten auseinander
            hält — oder als denselben erkennt. */}
        {bilder === 0 ? 'ohne Bild' : `${bilder} ${bilder === 1 ? 'Bild' : 'Bilder'}`}
      </p>
      <p className="leise zahl paar-id">{id}</p>
    </div>
  )
}
